import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Dumbbell,
  Flame,
  CheckCircle2,
  XCircle,
  Target,
  Salad,
  TrendingUp,
  Music2,
  ArrowRight,
  Lock,
} from "lucide-react";
import StatueSilhouette from "@/components/StatueSilhouette";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const IMG_1_BEFORE =
  "https://customer-assets.emergentagent.com/job_5c9577c4-4f65-481d-969b-b5547c5678d0/artifacts/0ta89d4w_image%20%283%29.png";
const IMG_2 =
  "https://customer-assets.emergentagent.com/job_5c9577c4-4f65-481d-969b-b5547c5678d0/artifacts/0fje4z56_image%20%284%29.png";
const IMG_3 =
  "https://customer-assets.emergentagent.com/job_5c9577c4-4f65-481d-969b-b5547c5678d0/artifacts/fopydsw9_image%20%285%29.png";
const IMG_4 =
  "https://customer-assets.emergentagent.com/job_5c9577c4-4f65-481d-969b-b5547c5678d0/artifacts/0mbuht8k_image%20%289%29.png";
// Reuse the strongest after shot as Image 5
const IMG_5 = IMG_2;

const TIKTOK_URL = "https://www.tiktok.com/@nick_stath0";

const Crosshair = ({ className }) => (
  <svg
    viewBox="0 0 24 24"
    className={`crosshair ${className || ""}`}
    fill="none"
    stroke="currentColor"
    strokeWidth="1.4"
  >
    <path d="M12 2v20M2 12h20" />
  </svg>
);

const Landing = () => {
  const [loading, setLoading] = useState(false);

  const handleCheckout = async () => {
    if (loading) return;
    setLoading(true);
    try {
      const origin = window.location.origin;
      const res = await axios.post(`${API}/checkout/session`, {
        package_id: "starter_blueprint",
        origin_url: origin,
      });
      if (res.data?.url) {
        window.location.href = res.data.url;
      } else {
        toast.error("Could not start checkout. Try again.");
        setLoading(false);
      }
    } catch (e) {
      console.error(e);
      toast.error("Checkout failed. Please try again.");
      setLoading(false);
    }
  };

  useEffect(() => {
    document.title = "Greek God Physique System – Starter Blueprint";
  }, []);

  return (
    <div className="relative min-h-screen bg-[#050505] text-white overflow-x-hidden">
      {/* Marquee ticker top */}
      <div className="relative z-10 border-b border-[#262626] bg-[#050505] py-2 overflow-hidden">
        <div className="marquee text-yellow font-mono text-xs uppercase tracking-[0.3em]">
          {Array.from({ length: 2 }).map((_, i) => (
            <div key={i} className="flex items-center gap-10 pr-10 whitespace-nowrap">
              <span>+ Greek God Physique System</span>
              <span>+ Starter Blueprint</span>
              <span>+ €25 One-Time · Lifetime Access</span>
              <span>+ Built From 10 Years In The Gym</span>
              <span>+ By Nick · 53 Years Old</span>
              <span>+ Greek God Physique System</span>
              <span>+ Starter Blueprint</span>
              <span>+ €25 One-Time · Lifetime Access</span>
            </div>
          ))}
        </div>
      </div>

      {/* ============ HERO ============ */}
      <section className="relative z-10 px-5 sm:px-10 lg:px-20 pt-12 pb-16 lg:pt-20 lg:pb-24 overflow-hidden">
        {/* Greek God statue photo — top right */}
        <div
          aria-hidden
          className="pointer-events-none absolute inset-y-0 right-0 z-0 flex items-center justify-end"
        >
          <div className="relative h-full w-[80vw] sm:w-[55vw] lg:w-[50vw] max-w-[820px]">
            <img
              src="https://customer-assets.emergentagent.com/job_god-body-starter/artifacts/pm62nn9t_image%20%2810%29.png"
              alt=""
              data-testid="hero-statue-image"
              className="absolute inset-0 h-full w-full object-cover object-center opacity-60 lg:opacity-80"
              style={{
                maskImage:
                  "radial-gradient(ellipse at 70% 50%, black 35%, transparent 80%)",
                WebkitMaskImage:
                  "radial-gradient(ellipse at 70% 50%, black 35%, transparent 80%)",
              }}
            />
            {/* Left fade into page background */}
            <div className="absolute inset-y-0 left-0 w-2/3 bg-gradient-to-r from-[#050505] via-[#050505]/85 to-transparent" />
            {/* Bottom fade */}
            <div className="absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-[#050505] to-transparent" />
            {/* Top fade */}
            <div className="absolute inset-x-0 top-0 h-1/4 bg-gradient-to-b from-[#050505] to-transparent" />
            {/* Yellow tint overlay for theme cohesion */}
            <div
              className="absolute inset-0 mix-blend-overlay opacity-30"
              style={{
                background:
                  "linear-gradient(135deg, rgba(255,223,0,0.25), transparent 60%)",
              }}
            />
          </div>
        </div>

        <Crosshair className="top-4 left-4" />
        <Crosshair className="top-4 right-4" />

        <div className="relative z-10 max-w-6xl mx-auto">
          <div className="flex items-center gap-3 mb-6 fade-up">
            <span className="font-mono text-[10px] sm:text-xs uppercase tracking-[0.3em] text-[#a1a1aa]">
              FILE — 001 / STARTER BLUEPRINT
            </span>
            <span className="brutal-divider flex-1" />
            <span className="font-mono text-[10px] sm:text-xs uppercase tracking-[0.3em] text-yellow">
              2016 — 2026
            </span>
          </div>

          <h1
            className="font-display headline-stack text-[15vw] sm:text-[11vw] lg:text-[9rem] leading-[0.85] tracking-tighter fade-up"
            data-testid="hero-title"
          >
            <span className="text-white">GREEK GOD</span>
            <span className="text-yellow">PHYSIQUE</span>
            <span className="headline-outline">SYSTEM.</span>
          </h1>

          <div className="mt-6 flex flex-wrap items-baseline gap-3">
            <span className="font-display text-3xl sm:text-4xl text-white">
              Starter Blueprint
            </span>
            <span className="font-display text-3xl sm:text-4xl text-yellow">
              · €25
            </span>
          </div>

          <div className="mt-8 max-w-2xl space-y-5 fade-up">
            <div className="flex flex-wrap items-center gap-4 pt-2">
              <button
                onClick={handleCheckout}
                disabled={loading}
                data-testid="hero-cta-checkout"
                className="btn-brutal pulse-yellow text-base sm:text-lg disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {loading ? "REDIRECTING..." : "GET ACCESS — €25"}
                <ArrowRight className="h-5 w-5" strokeWidth={2.5} />
              </button>
              <a
                href="#what-you-get"
                data-testid="hero-cta-learn"
                className="btn-ghost text-sm"
              >
                What's Inside
              </a>
            </div>

            <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#71717a] pt-1 flex items-center gap-2">
              <Lock className="h-3 w-3" /> Secure checkout · One-time payment · Lifetime access
            </p>
          </div>
        </div>
      </section>

      {/* ============ IMAGE 1 — BEFORE + Nick intro ============ */}
      <section className="relative z-10 px-5 sm:px-10 lg:px-20 pb-16 lg:pb-24">
        <div className="max-w-6xl mx-auto grid lg:grid-cols-12 gap-8 lg:gap-12 items-center">
          <div className="lg:col-span-5">
            <div className="photo-frame aspect-[4/5] max-h-[680px]">
              <span className="label">BEFORE · 2016</span>
              <img
                src={IMG_1_BEFORE}
                alt="Nick before transformation, 2016"
                data-testid="before-image"
                className="img-cover"
                style={{ filter: "grayscale(15%) contrast(1.05)", objectPosition: "center 20%" }}
              />
              <div className="absolute bottom-3 left-3 z-10 font-mono text-[10px] uppercase tracking-[0.25em] text-white/80">
                Year 01 · Starting Point
              </div>
            </div>
          </div>

          <div className="lg:col-span-7 space-y-5">
            <div className="font-mono text-xs uppercase tracking-[0.3em] text-yellow">
              01 / The Starting Point
            </div>
            <h2
              className="font-display text-4xl sm:text-5xl lg:text-6xl leading-none"
              data-testid="intro-title"
            >
              MY NAME IS <span className="text-yellow">NICK</span>.<br />
              I'M <span className="text-yellow">53</span>.
            </h2>
            <p className="text-base sm:text-lg text-[#d4d4d8] leading-relaxed">
              This is where I started. Confused, zero structure, guessing my way
              through the gym. <span className="text-yellow font-semibold">2016 vs. Now</span>{" "}
              — and the difference isn't just time. It's the system.
            </p>
            <p className="text-base sm:text-lg text-[#d4d4d8] leading-relaxed">
              Listen… I've been in the gym for over{" "}
              <span className="text-yellow font-semibold">10 years</span>. From{" "}
              <span className="font-mono">2016 to 2026</span>. And over that time,
              I figured out something most people never do:
            </p>
            <p className="text-lg sm:text-xl text-white leading-relaxed border-l-2 border-yellow pl-4">
              You don't need years of confusion, random workouts, and endless
              trial and error to build a powerful, aesthetic physique.
              <br />
              <span className="text-yellow">You just need the right system.</span>
            </p>
          </div>
        </div>
      </section>

      {/* ============ IMAGE 2 — Hero proof shot (editorial) ============ */}
      <section className="relative z-10 px-5 sm:px-10 lg:px-20 pb-16 lg:pb-24">
        <div className="max-w-6xl mx-auto grid lg:grid-cols-12 gap-8 lg:gap-12 items-center">
          {/* Text left */}
          <div className="lg:col-span-5 order-2 lg:order-1 space-y-5">
            <div className="font-mono text-xs uppercase tracking-[0.3em] text-yellow">
              The Outcome
            </div>
            <h3
              className="font-display text-5xl sm:text-6xl lg:text-7xl leading-[0.9]"
              data-testid="outcome-title"
            >
              THE <span className="text-yellow">SYSTEM</span><br />APPLIED.
            </h3>
            <p className="text-base sm:text-lg text-[#d4d4d8] leading-relaxed">
              No magic. No tricks. Just the right structure, repeated long enough
              to bend the body to your will.
            </p>
            <div className="grid grid-cols-3 gap-3 pt-2 max-w-md">
              <div className="border border-[#262626] p-3">
                <div className="font-display text-3xl text-yellow leading-none">10+</div>
                <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#a1a1aa] mt-1">Years Refined</div>
              </div>
              <div className="border border-[#262626] p-3">
                <div className="font-display text-3xl text-yellow leading-none">100%</div>
                <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#a1a1aa] mt-1">Natural</div>
              </div>
              <div className="border border-[#262626] p-3">
                <div className="font-display text-3xl text-yellow leading-none">€25</div>
                <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#a1a1aa] mt-1">One-Time</div>
              </div>
            </div>
          </div>

          {/* Portrait image right - natural aspect, not cropped */}
          <div className="lg:col-span-7 order-1 lg:order-2">
            <div className="photo-frame aspect-[4/5] max-h-[720px] mx-auto">
              <span className="label label-after">AFTER · THE SYSTEM</span>
              <img
                src={IMG_2}
                alt="Greek God Physique — the system applied"
                data-testid="after-image-1"
                className="img-cover"
                style={{ objectPosition: "center 30%" }}
              />
              <div className="absolute bottom-4 right-4 z-10 font-mono text-[10px] uppercase tracking-[0.25em] text-yellow">
                Result of the Blueprint
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============ THE REALITY ============ */}
      <section className="relative z-10 px-5 sm:px-10 lg:px-20 py-16 lg:py-24 border-t border-[#1a1a1a]">
        <div className="max-w-6xl mx-auto grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-4">
            <div className="font-mono text-xs uppercase tracking-[0.3em] text-yellow mb-4">
              02 / The Reality
            </div>
            <h2
              className="font-display text-5xl sm:text-6xl lg:text-7xl leading-none"
              data-testid="reality-title"
            >
              MOST PEOPLE STAY <span className="text-yellow">STUCK</span> FOR YEARS.
            </h2>
            <p className="font-mono text-sm text-[#a1a1aa] mt-6 leading-relaxed max-w-md">
              Because they keep repeating the same broken loop:
            </p>
          </div>

          <div className="lg:col-span-8 grid sm:grid-cols-2 gap-4">
            {[
              "Train without structure",
              "Follow random, generic workouts online",
              "Have no real diet or nutrition plan",
              "Stay stuck for years guessing",
            ].map((t, i) => (
              <div
                key={i}
                data-testid={`reality-item-${i}`}
                className="brutal-card flex items-start gap-3"
              >
                <XCircle className="h-5 w-5 text-yellow shrink-0 mt-1" strokeWidth={2} />
                <span className="text-[#e4e4e7] leading-relaxed">{t}</span>
              </div>
            ))}
            <p className="sm:col-span-2 font-mono text-sm text-[#a1a1aa] pt-4 leading-relaxed">
              I went through all of that. Instead of wasting more time, I built a
              simple blueprint that <span className="text-yellow">actually works</span>.
            </p>
          </div>
        </div>
      </section>

      {/* ============ IMAGE 3 & IMAGE 4 SIDE-BY-SIDE ============ */}
      <section className="relative z-10 px-5 sm:px-10 lg:px-20 pb-16 lg:pb-24">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
            <h3
              className="font-display text-4xl sm:text-5xl lg:text-6xl leading-none"
              data-testid="proof-title"
            >
              THE <span className="text-yellow">PROOF</span>.
            </h3>
            <p className="font-mono text-xs text-[#a1a1aa] max-w-xs">
              Real, natural, no shortcuts. Just the system applied — relentlessly.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 lg:gap-6">
            <div className="photo-frame aspect-[4/5] max-h-[680px] lg:translate-y-4">
              <span className="label label-after">AFTER</span>
              <img
                src={IMG_3}
                alt="After transformation side"
                data-testid="after-image-2"
                className="img-cover"
                style={{ objectPosition: "center 30%" }}
              />
            </div>
            <div className="photo-frame aspect-[4/5] max-h-[680px] lg:-translate-y-4">
              <span className="label label-after">AFTER</span>
              <img
                src={IMG_4}
                alt="After transformation front"
                data-testid="after-image-3"
                className="img-cover"
                style={{ objectPosition: "center 25%" }}
              />
            </div>
          </div>
        </div>
      </section>

      {/* ============ WHAT THIS IS ============ */}
      <section className="relative z-10 px-5 sm:px-10 lg:px-20 py-16 lg:py-24 border-t border-[#1a1a1a]">
        <div className="max-w-4xl mx-auto text-center">
          <div className="font-mono text-xs uppercase tracking-[0.3em] text-yellow mb-3">
            03 / What This Is
          </div>
          <h2
            className="font-display text-5xl sm:text-6xl lg:text-7xl leading-none"
            data-testid="what-is-title"
          >
            THE STARTER <span className="text-yellow">BLUEPRINT</span>.
          </h2>
          <p className="text-base sm:text-lg text-[#d4d4d8] mt-6 leading-relaxed">
            This is the Starter Blueprint of the{" "}
            <span className="text-yellow">Greek God Physique System</span>. It
            gives you the exact foundation you need to start transforming your
            body the right way — without overthinking everything.
          </p>
          <p className="text-base sm:text-lg text-white mt-4 font-medium leading-relaxed">
            No bs. No confusion. Just a proven system you can follow day in and day out.
          </p>
        </div>
      </section>

      {/* ============ WHAT YOU GET ============ */}
      <section
        id="what-you-get"
        className="relative z-10 px-5 sm:px-10 lg:px-20 py-16 lg:py-24 border-t border-[#1a1a1a]"
      >
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 flex items-center justify-start pl-0 lg:pl-10 z-0"
        >
          <StatueSilhouette className="text-yellow opacity-[0.04] h-[80vh] w-auto" />
        </div>

        <div className="relative z-10 max-w-6xl mx-auto">
          <div className="font-mono text-xs uppercase tracking-[0.3em] text-yellow mb-3">
            04 / What You Get
          </div>
          <h2
            className="font-display text-5xl sm:text-6xl lg:text-7xl leading-none mb-12 max-w-3xl"
            data-testid="what-you-get-title"
          >
            INSIDE THE <span className="text-yellow">BLUEPRINT</span>.
          </h2>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              {
                icon: Dumbbell,
                title: "Structured Workout Plan",
                desc: "Simple, effective, and built to maximize your time in the gym.",
              },
              {
                icon: Target,
                title: "The Ultimate Training Split",
                desc: "Designed specifically for building lean muscle while dropping fat.",
              },
              {
                icon: Salad,
                title: "Diet & Nutrition Guidance",
                desc: "No starvation. Just sustainable eating habits that actually work.",
              },
              {
                icon: Flame,
                title: "Daily Meal Structure",
                desc: "A clear, repeatable plan you can follow effortlessly every day.",
              },
              {
                icon: TrendingUp,
                title: "Clear Progression System",
                desc: "Know exactly how to level up your strength week after week.",
              },
              {
                icon: CheckCircle2,
                title: "Lifetime Access",
                desc: "One payment. Keep the Blueprint forever. Revisit any time.",
              },
            ].map((item, i) => {
              const Icon = item.icon;
              return (
                <div
                  key={i}
                  className="brutal-card group"
                  data-testid={`benefit-card-${i}`}
                >
                  <div className="flex items-start gap-4">
                    <div className="h-10 w-10 border border-yellow flex items-center justify-center text-yellow shrink-0 group-hover:bg-yellow group-hover:text-[#050505] transition">
                      <Icon className="h-5 w-5" strokeWidth={2} />
                    </div>
                    <div>
                      <h3 className="font-display text-2xl tracking-wide uppercase">
                        {item.title}
                      </h3>
                      <p className="text-sm text-[#a1a1aa] mt-2 leading-relaxed">
                        {item.desc}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ============ IMAGE 5 — Natural / chiseled (editorial reverse) ============ */}
      <section className="relative z-10 px-5 sm:px-10 lg:px-20 pb-16 lg:pb-24">
        <div className="max-w-6xl mx-auto grid lg:grid-cols-12 gap-8 lg:gap-12 items-center">
          {/* Portrait image left */}
          <div className="lg:col-span-7">
            <div className="photo-frame aspect-[4/5] max-h-[720px] mx-auto">
              <span className="label label-after">CHISELED · NATURAL</span>
              <img
                src={IMG_5}
                alt="Aesthetic Greek God physique"
                data-testid="after-image-5"
                className="img-cover"
                style={{ objectPosition: "center 25%" }}
              />
              <div className="absolute bottom-4 left-4 z-10 font-mono text-[10px] uppercase tracking-[0.25em] text-yellow">
                The other side of the system
              </div>
            </div>
          </div>

          {/* Quote right */}
          <div className="lg:col-span-5 space-y-5">
            <div className="font-mono text-xs uppercase tracking-[0.3em] text-yellow">
              The Standard
            </div>
            <h3
              className="font-display text-5xl sm:text-6xl lg:text-7xl leading-[0.9]"
              data-testid="standard-title"
            >
              LEAN.<br />
              <span className="text-yellow">AESTHETIC.</span><br />
              NATURAL.
            </h3>
            <p className="text-base sm:text-lg text-[#d4d4d8] leading-relaxed border-l-2 border-yellow pl-4">
              This isn't a fantasy body. It's the body the right system + time +
              consistency builds. The Starter Blueprint is step one.
            </p>
          </div>
        </div>
      </section>

      {/* ============ WHO THIS IS FOR ============ */}
      <section className="relative z-10 px-5 sm:px-10 lg:px-20 py-16 lg:py-24 border-t border-[#1a1a1a]">
        <div className="max-w-6xl mx-auto grid lg:grid-cols-2 gap-12 items-start">
          <div>
            <div className="font-mono text-xs uppercase tracking-[0.3em] text-yellow mb-3">
              05 / Who It's For
            </div>
            <h2
              className="font-display text-5xl sm:text-6xl lg:text-7xl leading-none"
              data-testid="for-who-title"
            >
              IF THIS IS <span className="text-yellow">YOU</span>...
            </h2>
            <p className="font-mono text-sm text-[#a1a1aa] mt-6 leading-relaxed max-w-md">
              The Starter Blueprint isn't for everyone. It's for the people serious
              about ending the guesswork and starting real.
            </p>
          </div>

          <div className="space-y-3">
            {[
              "Beginners who want to skip the 3-year learning curve",
              "Lifters tired of guessing what to do the second they walk into the gym",
              "Anyone who wants to build a lean, aesthetic, chiseled physique — naturally",
            ].map((t, i) => (
              <div
                key={i}
                className="flex items-center gap-4 border border-[#262626] p-4 hover:border-yellow transition"
                data-testid={`for-who-item-${i}`}
              >
                <CheckCircle2
                  className="h-5 w-5 text-yellow shrink-0"
                  strokeWidth={2}
                />
                <span className="text-base text-[#e4e4e7]">{t}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ FINAL MESSAGE / CTA ============ */}
      <section className="relative z-10 px-5 sm:px-10 lg:px-20 py-20 lg:py-28 border-t border-[#1a1a1a] overflow-hidden">
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 flex items-center justify-center z-0"
        >
          <StatueSilhouette className="text-yellow opacity-[0.05] h-[110vh] w-auto" />
        </div>

        <div className="relative z-10 max-w-4xl mx-auto text-center">
          <div className="font-mono text-xs uppercase tracking-[0.3em] text-yellow mb-3">
            06 / Final Message
          </div>
          <h2
            className="font-display text-5xl sm:text-7xl lg:text-8xl leading-[0.9]"
            data-testid="final-cta-title"
          >
            I SPENT 10+ YEARS. <br />
            <span className="text-yellow">YOU DON'T HAVE TO.</span>
          </h2>
          <p className="text-base sm:text-lg text-[#d4d4d8] mt-6 max-w-2xl mx-auto leading-relaxed">
            This blueprint gives you the starting map so you can build momentum
            fast, stop wasting your workouts, and actually see results in the
            mirror.
          </p>

          <div className="mt-10 inline-flex flex-col items-center gap-4">
            <div className="flex items-baseline gap-3">
              <span className="font-display text-6xl sm:text-8xl text-yellow leading-none">
                €25
              </span>
              <span className="font-mono text-xs uppercase tracking-[0.3em] text-[#a1a1aa]">
                One-time · Lifetime access
              </span>
            </div>

            <button
              onClick={handleCheckout}
              disabled={loading}
              data-testid="final-cta-checkout"
              className="btn-brutal text-lg sm:text-xl pulse-yellow disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {loading ? "REDIRECTING..." : "START BUILDING YOUR PHYSIQUE TODAY"}
              <ArrowRight className="h-5 w-5" strokeWidth={2.5} />
            </button>

            <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#71717a] flex items-center gap-2">
              <Lock className="h-3 w-3" /> Secured by Stripe · Instant access
            </p>
            <p className="font-mono text-xs uppercase tracking-[0.2em] text-[#a1a1aa] mt-2">
              No guesswork. Just the foundation.
            </p>
          </div>
        </div>
      </section>

      {/* ============ FOOTER ============ */}
      <footer className="relative z-10 px-5 sm:px-10 lg:px-20 py-12 border-t border-[#1a1a1a] bg-[#050505]">
        <div className="max-w-6xl mx-auto flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6">
          <div>
            <div className="font-display text-2xl tracking-tight">
              GREEK GOD <span className="text-yellow">PHYSIQUE</span> SYSTEM
            </div>
            <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#71717a] mt-2">
              Questions? DM Nick personally on TikTok.
            </p>
          </div>
          <a
            href={TIKTOK_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="tiktok-link"
            className="btn-ghost"
          >
            <Music2 className="h-4 w-4" />
            @nick_stath0
          </a>
        </div>
        <div className="max-w-6xl mx-auto mt-10 pt-6 border-t border-[#1a1a1a] font-mono text-[10px] uppercase tracking-[0.25em] text-[#52525b] flex flex-wrap justify-between gap-2">
          <span>© {new Date().getFullYear()} Greek God Physique System · Nick · Age 53</span>
          <span>Built for those who want more</span>
        </div>
      </footer>

      {/* Sticky mobile CTA */}
      <div className="sticky-cta" data-testid="sticky-cta">
        <div className="flex items-baseline gap-2">
          <span className="font-display text-3xl text-yellow leading-none">€25</span>
          <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#a1a1aa]">
            lifetime
          </span>
        </div>
        <button
          onClick={handleCheckout}
          disabled={loading}
          data-testid="sticky-cta-checkout"
          className="btn-brutal !py-3 !px-4 text-sm disabled:opacity-60"
        >
          {loading ? "..." : "START NOW"}
          <ArrowRight className="h-4 w-4" strokeWidth={2.5} />
        </button>
      </div>

      <div className="h-20 lg:h-0" />
    </div>
  );
};

export default Landing;
